import { useState, useEffect, useCallback } from 'react';
import { api, tokenStorage } from '../api/client';

export function useVendorProfiles({ shopId, approvalStatus, category } = {}) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!tokenStorage.getAccess()) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (shopId) params.set('shop_id', shopId);
      if (approvalStatus) params.set('approval_status', approvalStatus);
      if (category) params.set('category', category);
      const qs = params.toString();
      const data = await api.get(`/api/vendor-profiles${qs ? '?' + qs : ''}`);
      setProfiles(data.profiles || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [shopId, approvalStatus, category]);

  useEffect(() => { refresh(); }, [refresh]);

  return { profiles, loading, error, refresh };
}

export function useUnprofiledVendors(shopId) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!tokenStorage.getAccess()) return;
    setLoading(true);
    try {
      const params = shopId ? `?shop_id=${shopId}` : '';
      const data = await api.get(`/api/vendor-profiles/needed/list${params}`);
      setVendors(data.vendors || []);
    } catch {
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { vendors, loading, refresh };
}

export function useDocumentReviews({ shopId, status = 'PENDING' } = {}) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!tokenStorage.getAccess()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ status });
      if (shopId) params.set('shop_id', shopId);
      const data = await api.get(`/api/document-review/queue?${params}`);
      setReviews(data.reviews || []);
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [shopId, status]);

  useEffect(() => { refresh(); }, [refresh]);

  return { reviews, loading, refresh };
}

export function useCodChecks({ shopId, auditStatus } = {}) {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!tokenStorage.getAccess()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (shopId) params.set('shop_id', shopId);
      if (auditStatus) params.set('audit_status', auditStatus);
      const qs = params.toString();
      const data = await api.get(`/api/auditor/cod-checks-queue${qs ? '?' + qs : ''}`);
      setChecks(data.checks || []);
    } catch {
      setChecks([]);
    } finally {
      setLoading(false);
    }
  }, [shopId, auditStatus]);

  useEffect(() => { refresh(); }, [refresh]);

  return { checks, loading, refresh };
}

export function useCodChecksSummary(shopId) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!tokenStorage.getAccess()) return;
    setLoading(true);
    try {
      const params = shopId ? `?shop_id=${shopId}` : '';
      const data = await api.get(`/api/auditor/cod-checks-summary${params}`);
      setSummary(data);
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { summary, loading, refresh };
}
